import { useEffect, useCallback, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { chainManager } from '../ChainManager';
import { SupportedChain } from '../../constants';
import { setWallet } from '../../features/wallet/walletSlice';
import {
  setCurrentChain,
  setBnbConnected,
  setSolanaConnected,
  setBnbAccount,
  setSolanaAccount,
  setBnbBalance,
  setSolanaBalance,
  setLoading,
  setError,
  clearError,
} from '../../features/chain/chainSlice';
import { AppDispatch, RootState } from '../../app/store';
import { WalletInfo } from '../interfaces/ChainAdapter';

// Global state manager to prevent multiple instances
class GlobalChainStateManager {
  private static instance: GlobalChainStateManager;
  private listeners: Map<string, (() => void)[]> = new Map();
  private isInitialized = false;
  private dispatch: AppDispatch | null = null;
  private _componentCount = 0;

  static getInstance(): GlobalChainStateManager {
    if (!GlobalChainStateManager.instance) {
      GlobalChainStateManager.instance = new GlobalChainStateManager();
    }
    return GlobalChainStateManager.instance;
  }

  registerComponent(dispatch: AppDispatch): void {
    this._componentCount++;
    this.dispatch = dispatch;

    if (this.isInitialized) {
      return;
    }

    const handleWalletChange = (walletInfo: WalletInfo | null) => {
      if (walletInfo && this.dispatch) {
        this.dispatch(
          setWallet({
            address: walletInfo.address,
            balance: parseFloat(walletInfo.balance),
            network: walletInfo.chain,
            symbol: walletInfo.symbol,
          }),
        );
      } else if (this.dispatch) {
        this.dispatch(setWallet(null));
      }
    };

    const handleChainChange = () => {
      // Clear wallet when switching chains
      if (this.dispatch) {
        this.dispatch(setWallet(null));
      }
    };

    const handleWalletError = (error: unknown) => {
      console.error('Wallet error event received:', error);
      if (this.dispatch) {
        this.dispatch(
          setError(
            `Wallet error: ${
              error instanceof Error ? error.message : 'Unknown error'
            }`,
          ),
        );
      }
    };

    // Subscribe to events
    const unsubscribeWallet = chainManager.on(
      'walletChanged',
      handleWalletChange,
    );
    const unsubscribeChain = chainManager.on('chainChanged', handleChainChange);
    const unsubscribeError = chainManager.on('walletError', handleWalletError);

    // Store unsubscribers
    this.listeners.set('walletChanged', [unsubscribeWallet]);
    this.listeners.set('chainChanged', [unsubscribeChain]);
    this.listeners.set('walletError', [unsubscribeError]);

    this.isInitialized = true;
  }

  unregisterComponent(): void {
    this._componentCount--;

    if (this._componentCount <= 0) {
      this.cleanup();
    }
  }

  get componentCount(): number {
    return this._componentCount;
  }

  cleanup(): void {
    this.listeners.forEach(unsubscribers => {
      unsubscribers.forEach(unsubscribe => {
        try {
          unsubscribe();
        } catch (error) {
          console.warn('Failed to cleanup global listener:', error);
        }
      });
    });
    this.listeners.clear();
    this.isInitialized = false;
    this._componentCount = 0;
    this.dispatch = null;
  }
}

export const useChainManager = () => {
  const dispatch = useDispatch<AppDispatch>();
  const wallet = useSelector((state: RootState) => state.wallet);
  const chain = useSelector((state: RootState) => state.chain);
  const currentChain = chain.currentChain;

  // State for wallet conflict modal
  const [showWalletConflictModal, setShowWalletConflictModal] = useState(false);
  const [conflictTargetChain, setConflictTargetChain] =
    useState<SupportedChain>('bnb');

  // Ref to prevent multiple initializations
  const initializedRef = useRef(false);

  // Register component with global state manager
  useEffect(() => {
    const globalStateManager = GlobalChainStateManager.getInstance();
    globalStateManager.registerComponent(dispatch);

    // Cleanup on unmount
    return () => {
      globalStateManager.unregisterComponent();
    };
  }, [dispatch]);

  // Initialize wallet connection on mount - only sync if already connected
  useEffect(() => {
    const initializeWallet = async () => {
      if (initializedRef.current) {
        return;
      }

      try {
        const currentChain = chainManager.getCurrentChain();

        // Sync Redux state with ChainManager
        if (currentChain !== chain.currentChain) {
          dispatch(setCurrentChain(currentChain));
        }

        // Only check if wallet is connected, don't trigger connection
        const isConnected = await chainManager.isWalletConnected();

        if (isConnected) {
          const walletInfo = await chainManager.getWalletInfo();
          if (walletInfo) {
            dispatch(
              setWallet({
                address: walletInfo.address,
                balance: parseFloat(walletInfo.balance),
                network: walletInfo.chain,
                symbol: walletInfo.symbol,
              }),
            );
          }
        }

        initializedRef.current = true;
      } catch (error) {
        console.error('Failed to initialize wallet:', error);
      }
    };

    // Only initialize if this is the first component to use useChainManager
    const globalStateManager = GlobalChainStateManager.getInstance();
    if (globalStateManager.componentCount === 1) {
      initializeWallet();
    }
  }, [dispatch, chain.currentChain]);

  // Internal: update chain-specific state in a generic way
  const applyChainConnectionState = useCallback(
    (
      chainName: SupportedChain,
      params: { address: string; balance: string },
    ) => {
      const handlers: Record<SupportedChain, () => void> = {
        bnb: () => {
          dispatch(setBnbConnected(true));
          dispatch(setBnbAccount(params.address));
          dispatch(setBnbBalance(params.balance));
        },
        solana: () => {
          dispatch(setSolanaConnected(true));
          dispatch(setSolanaAccount(params.address));
          dispatch(setSolanaBalance(params.balance));
        },
      };
      handlers[chainName]();
    },
    [dispatch],
  );

  const clearChainConnectionState = useCallback(
    (chainName: SupportedChain) => {
      const handlers: Record<SupportedChain, () => void> = {
        bnb: () => {
          dispatch(setBnbConnected(false));
          dispatch(setBnbAccount(null));
          dispatch(setBnbBalance('0'));
        },
        solana: () => {
          dispatch(setSolanaConnected(false));
          dispatch(setSolanaAccount(null));
          dispatch(setSolanaBalance('0'));
        },
      };
      handlers[chainName]();
    },
    [dispatch],
  );

  // Connect wallet
  const connectWallet = useCallback(async () => {
    try {
      dispatch(setLoading(true));
      dispatch(clearError());

      const walletInfo = await chainManager.connectWallet();
      dispatch(
        setWallet({
          address: walletInfo.address,
          balance: parseFloat(walletInfo.balance),
          network: walletInfo.chain,
          symbol: walletInfo.symbol,
        }),
      );

      // Update chain-specific state via mapping
      applyChainConnectionState(currentChain as SupportedChain, {
        address: walletInfo.address,
        balance: walletInfo.balance,
      });

      return walletInfo;
    } catch (error) {
      console.error('Failed to connect wallet:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';

      // Check if it's a wallet conflict error
      const isWalletConflict =
        // BNB/MetaMask related errors (Vietnamese messages)
        errorMessage.includes('MetaMask không được phát hiện') ||
        errorMessage.includes('MetaMask không được tìm thấy') ||
        errorMessage.includes('Provider không phải MetaMask') ||
        errorMessage.includes('MetaMask not found') ||
        // Solana/Phantom related errors
        errorMessage.includes('Phantom wallet not found') ||
        errorMessage.includes('Phantom wallet detected') ||
        errorMessage.includes(
          'This website is trying to use Ethereum, but your Phantom wallet is configured for Solana',
        ) ||
        // Generic wallet detection errors
        errorMessage.includes('Wallet not found') ||
        errorMessage.includes('Wallet not detected');

      if (isWalletConflict) {
        setConflictTargetChain(currentChain as SupportedChain);
        setShowWalletConflictModal(true);
      }

      dispatch(setError(`Failed to connect wallet: ${errorMessage}`));
      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  }, [applyChainConnectionState, currentChain, dispatch]);

  // Disconnect wallet
  const disconnectWallet = useCallback(async () => {
    try {
      dispatch(setLoading(true));
      dispatch(clearError());

      await chainManager.disconnectWallet();
      dispatch(setWallet(null));

      // Update chain-specific state via mapping
      clearChainConnectionState(currentChain as SupportedChain);
    } catch (error) {
      console.error('Failed to disconnect wallet:', error);
      dispatch(
        setError(
          `Failed to disconnect wallet: ${
            error instanceof Error ? error.message : 'Unknown error'
          }`,
        ),
      );
      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  }, [clearChainConnectionState, currentChain, dispatch]);

  // Switch chain
  const switchChain = useCallback(
    async (chain: SupportedChain) => {
      try {
        dispatch(setLoading(true));
        dispatch(clearError());

        await chainManager.switchChain(chain);
        dispatch(setCurrentChain(chain));

        // Clear wallet state when switching chains
        dispatch(setWallet(null));
        // Clear both chains for safety
        clearChainConnectionState('bnb');
        clearChainConnectionState('solana');
      } catch (error) {
        console.error('Failed to switch chain:', error);
        dispatch(
          setError(
            `Failed to switch to ${chain}: ${
              error instanceof Error ? error.message : 'Unknown error'
            }`,
          ),
        );
        throw error;
      } finally {
        dispatch(setLoading(false));
      }
    },
    [clearChainConnectionState, dispatch],
  );

  // Get current chain
  const getCurrentChain = useCallback(() => {
    return chainManager.getCurrentChain();
  }, []);

  // Get supported chains
  const getSupportedChains = useCallback(() => {
    return chainManager.getSupportedChains();
  }, []);

  // Check if wallet is connected
  const isWalletConnected = useCallback(async () => {
    return chainManager.isWalletConnected();
  }, []);

  // Get wallet info
  const getWalletInfo = useCallback(async () => {
    return chainManager.getWalletInfo();
  }, []);

  // Reset wallet state (useful for error recovery)
  const resetWalletState = useCallback(() => {
    dispatch(setWallet(null));
    dispatch(setBnbConnected(false));
    dispatch(setSolanaConnected(false));
    dispatch(setBnbAccount(null));
    dispatch(setSolanaAccount(null));
    dispatch(setBnbBalance('0'));
    dispatch(setSolanaBalance('0'));
    dispatch(clearError());
  }, [dispatch]);

  return {
    // State
    wallet,
    currentChain,
    chain,

    // Wallet conflict modal state
    showWalletConflictModal,
    conflictTargetChain,
    setShowWalletConflictModal,

    // Actions
    connectWallet,
    disconnectWallet,
    switchChain,
    getCurrentChain,
    getSupportedChains,
    isWalletConnected,
    getWalletInfo,
    resetWalletState,

    // Chain manager instance for direct access if needed
    chainManager,

    // Delegate all other methods to chainManager
    buyTickets: chainManager.buyTickets.bind(chainManager),
    claimLixi: chainManager.claimLixi.bind(chainManager),
    claimLixiByOwner: chainManager.claimLixiByOwner
      ? chainManager.claimLixiByOwner.bind(chainManager)
      : undefined,
    claimGlobalJackpot: chainManager.claimGlobalJackpot.bind(chainManager),
    claimLocalJackpot: chainManager.claimLocalJackpot.bind(chainManager),
    addWhitelister: chainManager.addWhitelister.bind(chainManager),
    removeWhitelister: chainManager.removeWhitelister.bind(chainManager),
    addManyWhitelisters: chainManager.addManyWhitelisters.bind(chainManager),
    setTicketPrice: chainManager.setTicketPrice.bind(chainManager),
    setClosingTime: chainManager.setClosingTime.bind(chainManager),
    setAdminStatus: chainManager.setAdminStatus.bind(chainManager),
    setJackpotConfig: chainManager.setJackpotConfig.bind(chainManager),
    setWinningHash: chainManager.setWinningHash.bind(chainManager),
    getTicketInfo: chainManager.getTicketInfo.bind(chainManager),
    getGroupInfo: chainManager.getGroupInfo.bind(chainManager),
    getGlobalJackpotPrice:
      chainManager.getGlobalJackpotPrice.bind(chainManager),
    isWhitelisted: chainManager.isWhitelisted.bind(chainManager),
    signMessage: chainManager.signMessage.bind(chainManager),
  };
};
